/**
 * This library is the only place where you'd use raw analytics from atlaskit
 */

import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';
import {
	SCREEN_EVENT_TYPE,
	TRACK_EVENT_TYPE,
	OPERATIONAL_EVENT_TYPE,
	UI_EVENT_TYPE,
	type EventTypes,
} from '../constants.tsx';
import type { AnalyticsEvent } from '../types.tsx';
import {
	getAnalyticsType,
	getAttributes,
	getSource,
	getActionSubject,
	getAction,
	getActionSubjectId,
	getContainerId,
	getContainers,
	getContainerType,
	getObjectId,
	getObjectType,
	getTags,
} from './extract-data-from-event.tsx';

export type MyEvent = {
	type: EventTypes | undefined;
	payload: {
		source?: string | undefined;
		[key: string]: unknown;
	};
};
/**
 * Transforms analytics events into structured formats suitable for further processing.
 * It enriches event data by organizing essential attributes and categorizing events
 * into distinct types, facilitating easier analysis and handling.
 */
export const getEvent = (event: AnalyticsEvent): MyEvent => {
	const analyticsType = getAnalyticsType(event);
	const attributes = getAttributes(event);
	const containers = getContainers(event);
	const source: string | undefined = getSource(event);
	const actionSubject = getActionSubject(event);
	if (analyticsType === SCREEN_EVENT_TYPE) {
		return {
			type: SCREEN_EVENT_TYPE,
			payload: {
				name: source,
				attributes,
				containers,
			},
		};
	}
	switch (analyticsType) {
		case UI_EVENT_TYPE:
		case TRACK_EVENT_TYPE:
		case OPERATIONAL_EVENT_TYPE: {
			const action = getAction(event);
			const actionSubjectId = getActionSubjectId(event);
			const containerId = getContainerId(event);
			const containerType = getContainerType(event);
			const objectId = getObjectId(event);
			const objectType = getObjectType(event);
			const tags = getTags(event);
			return {
				type: analyticsType,
				payload: omitBy(
					{
						action,
						actionSubject,
						actionSubjectId,
						source,
						tags,
						containerId,
						containers,
						containerType,
						objectType,
						objectId,
						attributes,
					},
					isUndefined,
				),
			};
		}
		default: {
			return {
				payload: {
					source,
					actionSubject,
					attributes,
					containers,
				},
				type: undefined,
			};
		}
	}
};
